/**
 * Gift Orders Module
 * Allows clients to buy food from a provider on behalf of another client ("send a meal gift").
 * Uses the Firestore modular Web SDK via the global `window.db` exposed by firebase-sdk-bootstrap.js.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-firestore.js";

export const GIFT_ORDER_STATUSES = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  PREPARING: "preparing",
  READY: "ready",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

export const GIFT_PAYMENT_STATUSES = {
  UNPAID: "unpaid",
  PAID: "paid",
  REFUNDED: "refunded",
};

/**
 * Create a new gift order document in Firestore.
 * Payment processing is handled separately via the Cloud Function.
 *
 * @param {object} db - Firestore instance
 * @param {object} params
 * @param {string} params.senderId
 * @param {string} params.senderName
 * @param {string} params.recipientId
 * @param {string} params.recipientEmail
 * @param {string} params.providerId
 * @param {string} params.providerName
 * @param {Array}  params.items  - [{ menuItemId, title, quantity, price }]
 * @param {number} params.totalAmount
 * @param {string} params.currency
 * @param {string} [params.message]
 * @param {object} [params.compliance]  - market compliance data (tax etc.)
 * @returns {Promise<string>} giftOrderId
 */
export async function createGiftOrder(db, {
  senderId,
  senderName,
  recipientId,
  recipientEmail,
  providerId,
  providerName,
  items,
  totalAmount,
  currency = "USD",
  message = "",
  compliance = {},
}) {
  if (!senderId || !recipientId || !providerId) {
    throw new Error("senderId, recipientId, and providerId are required.");
  }
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("At least one item is required.");
  }
  if (typeof totalAmount !== "number" || totalAmount <= 0) {
    throw new Error("totalAmount must be a positive number.");
  }

  const docRef = await addDoc(collection(db, "giftOrders"), {
    senderId,
    senderName: senderName || "",
    recipientId,
    recipientEmail: recipientEmail || "",
    providerId,
    providerName: providerName || "",
    items,
    message,
    totalAmount,
    currency,
    status: GIFT_ORDER_STATUSES.PENDING,
    paymentStatus: GIFT_PAYMENT_STATUSES.UNPAID,
    stripePaymentIntentId: null,
    compliance,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Cancel a gift order (sender or admin only — enforced by Firestore rules).
 */
export async function cancelGiftOrder(db, giftOrderId) {
  await updateDoc(doc(db, "giftOrders", giftOrderId), {
    status: GIFT_ORDER_STATUSES.CANCELLED,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Accept a gift order (recipient acknowledges/accepts the gift).
 */
export async function acceptGiftOrder(db, giftOrderId) {
  await updateDoc(doc(db, "giftOrders", giftOrderId), {
    status: GIFT_ORDER_STATUSES.ACCEPTED,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Real-time listener: gift orders sent BY this client.
 * @param {object} db
 * @param {string} senderId
 * @param {function} callback  - receives array of gift order objects
 * @returns {function} unsubscribe
 */
export function listenToSentGifts(db, senderId, callback) {
  const q = query(
    collection(db, "giftOrders"),
    where("senderId", "==", senderId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    const gifts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(gifts);
  });
}

/**
 * Real-time listener: gift orders received BY this client.
 * @param {object} db
 * @param {string} recipientId
 * @param {function} callback  - receives array of gift order objects
 * @returns {function} unsubscribe
 */
export function listenToReceivedGifts(db, recipientId, callback) {
  const q = query(
    collection(db, "giftOrders"),
    where("recipientId", "==", recipientId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    const gifts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(gifts);
  });
}
